import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@mui/material';
import {
  AssuranceLevel,
  DocumentationType,
  ZodIssueMetadata,
} from '@safc/configuration';
import { Button } from 'components/ui/buttons/Button/Button.js';
import { FileUploadApi } from 'components/ui/controlled-form/FileUploadApi/index.js';
import { RadioGroupForm } from 'components/ui/controlled-form/RadioGroupForm/index.js';
import { Tabs } from 'features/units/components/Tabs/index.js';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  getIssueModalSettings,
  type IssueModalMode,
} from '../issueModalMode.js';
import {
  FieldsWrapper,
  Footer,
  FooterContent,
} from './components/IssueForm.styled.js';
import { IssueFormNext } from './components/IssueFormNext.js';
import { IssueFormRow } from './components/IssueFormRow.js';
import { IssueFormTabLabel } from './components/IssueFormTabLabel.js';
import { issueFormCleanup } from './issueFormCleanup.js';
import { useIssueFormErrors } from './issueFormErrors.js';
import { IssueFormBatch } from './steps/IssueFormBatch.js';
import { IssueFormBlending } from './steps/IssueFormBlending.js';
import { IssueFormCertification } from './steps/IssueFormCertification.js';
import {
  ghgDependenciesFields,
  IssueFormGreenhouseGas,
} from './steps/IssueFormGreenhouseGas.js';
import { IssueFormProduct } from './steps/IssueFormProduct.js';
import { IssueFormSupplier } from './steps/IssueFormSupplier.js';

export function IssueForm(props: {
  initialIssueMetadata: Partial<ZodIssueMetadata>;
  onSubmit: (data: ZodIssueMetadata) => void;
  mode: IssueModalMode;
}) {
  const { shouldValidate, showAdminFields } = getIssueModalSettings(props.mode);
  const { errorModal, setErrors, hasTabError } = useIssueFormErrors();
  const onSubmit = (data: ZodIssueMetadata) => {
    setErrors(null);
    props.onSubmit(issueFormCleanup(data));
  };
  const { control, watch, setValue, handleSubmit, subscribe } = useForm<ZodIssueMetadata>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: shouldValidate ? zodResolver(ZodIssueMetadata) : undefined,
    defaultValues: {
      ...props.initialIssueMetadata,
    },
  });

  const ghgDependenciesValues = {
    isSolidWaste: watch(ghgDependenciesFields.isSolidWaste),
    certificationScheme: watch(ghgDependenciesFields.certificationScheme),
    certificationType: watch(ghgDependenciesFields.certificationType),
  };

  const isGhgTabVisible = Object.values(ghgDependenciesValues).every(dep => dep !== undefined);

  useEffect(() => {
    return subscribe({
      name: Object.values(ghgDependenciesFields), callback: () => {
        // entire GHG should be cleared, because otherwise we would save
        // old GHG tab values (already set, eg in IssueSimilar) in unit metadata (the form data would not be cleared)
        setValue('greenhouseGas', undefined as any);
      },
    });
  }, [subscribe]);

  return (
    <>
      {errorModal}
      <Grid container mb={2}>
        <IssueFormRow control={control} field="documentationType">
          {props => (
            <RadioGroupForm
              control={props.control}
              field={{
                ...props.field,
                options: [
                  { label: 'Proof of Sustainability', value: DocumentationType.PoS },
                  { label: 'Proof of Compliance', value: DocumentationType.PoC },
                ],
              }}
            />
          )}
        </IssueFormRow>
        {showAdminFields && (
          <IssueFormRow control={control} field="assuranceLevel">
            {props => (
              <RadioGroupForm
                control={props.control}
                field={{
                  ...props.field,
                  options: [
                    { label: AssuranceLevel.VAL, value: AssuranceLevel.VAL },
                    { label: AssuranceLevel.UR, value: AssuranceLevel.UR },
                    { label: AssuranceLevel.VER, value: AssuranceLevel.VER },
                    { label: AssuranceLevel.REV, value: AssuranceLevel.REV },
                  ],
                }}
              />
            )}
          </IssueFormRow>
        )}
        <IssueFormRow control={control} field="fileTool">
          {payload => (
            <FileUploadApi
              control={payload.control}
              field={{
                ...payload.field,
                fileProps: {
                  loadPreview: showAdminFields,
                  accept: {
                    'text/csv': ['.csv'],
                    'application/pdf': ['.pdf'],
                  },
                  maxSize: 10485760,
                },
              }}
            />
          )}
        </IssueFormRow>
        <Tabs
          tabs={[
            {
              label: <IssueFormTabLabel label="Batch" hasError={hasTabError('batch')} />,
              value: 'batch',
            },
            {
              label: <IssueFormTabLabel label="Blending" hasError={hasTabError('blending')} />,
              value: 'blending',
            },
            {
              label: <IssueFormTabLabel label="Supplier" hasError={hasTabError('supplier')} />,
              value: 'supplier',
            },
            {
              label: <IssueFormTabLabel label="Product information" hasError={hasTabError('product')} />,
              value: 'product',
            },
            {
              label: <IssueFormTabLabel label="Certification" hasError={hasTabError('certification')} />,
              value: 'certification',
            },
            ...(isGhgTabVisible
              ? [{
                  label: <IssueFormTabLabel label="Greenhouse gas information" hasError={hasTabError('greenhouseGas')} />,
                  value: 'greenhouseGas',
                }]
              : []),
          ]}
        >
          {({ goToTab }) => [
            <FieldsWrapper value="batch" key="batch">
              <IssueFormBatch
                control={control}
                shouldValidate={shouldValidate}
                regulatorySchemes={watch('batch.regulatorySchemesDeclaration')}
                additionalityApproachType={watch('batch.additionalityApproach.type')}
              />
              <IssueFormNext onClick={() => goToTab('blending')} />
            </FieldsWrapper>,
            <FieldsWrapper value="blending" key="blending">
              <IssueFormBlending
                control={control}
                shouldValidate={shouldValidate}
                isBlended={watch('blending.isBlended')}
              />
              <IssueFormNext onClick={() => goToTab('supplier')} />
            </FieldsWrapper>,
            <FieldsWrapper value="supplier" key="supplier">
              <IssueFormSupplier control={control} />
              <IssueFormNext onClick={() => goToTab('product')} />
            </FieldsWrapper>,
            <FieldsWrapper value="product" key="product">
              <IssueFormProduct
                control={control}
                volumeOfNeatFuel={watch('product.volumeOfNeatFuel')}
                feedstockName={watch('product.feedStock.name')}
                conversionProcess={watch('product.conversionProcess')}
                feedstockCountries={watch('product.countries')}
                isSolidWaste={ghgDependenciesValues.isSolidWaste}
                setValue={setValue}
              />
              <IssueFormNext onClick={() => goToTab('certification')} />
            </FieldsWrapper>,
            <FieldsWrapper value="certification" key="certification">
              <IssueFormCertification
                shouldValidate={shouldValidate}
                control={control}
                scheme={ghgDependenciesValues.certificationScheme}
                setValue={setValue}
                isLanduseBasedCropBased={watch('product.feedStock.isLanduseBasedCropBased')}
                wasCertifiedAsProductionResidue={watch('product.feedStock.wasCertifiedAsProductionResidue')}
                additionalScheme={watch('certification.additionalScheme')}
                isLowDisplacementRiskConfirmed={watch('certification.isLowDisplacementRiskConfirmed')}
                isLowILUCRiskConfirmed={watch('certification.isLowILUCRiskConfirmed')}
              />
              {isGhgTabVisible && <IssueFormNext onClick={() => goToTab('greenhouseGas')} />}
            </FieldsWrapper>,
            <FieldsWrapper value="greenhouseGas" key="greenhouseGas">
              <IssueFormGreenhouseGas
                control={control}
                totalLCAValueincludeEmissionsFromDeliveryToAirport={watch('greenhouseGas.totalLCAValueincludeEmissionsFromDeliveryToAirport')}
                useDefaultValueForDeliveryToAirport={watch('greenhouseGas.useDefaultValueForDeliveryToAirport')}
                {...ghgDependenciesValues}
              />
            </FieldsWrapper>,
          ]}
        </Tabs>
        <Footer>
          <FooterContent>
            <Button
              onClick={handleSubmit(onSubmit, (err) => {
                setErrors(err);
              })}
              color="primary"
              variant="contained"
            >
              Next
            </Button>
          </FooterContent>
        </Footer>
      </Grid>
    </>
  );
}
